import { createRoot, useEffect, useMemo, useState } from '@wordpress/element';
import CalculatorForm from './components/CalculatorForm';
import { calculateRoom, calculateTotals } from './calc';

function FrontendCalculator({ initialRooms, initialUnits, modalId }) {
    const [rooms, setRooms] = useState(initialRooms);
    const [units, setUnits] = useState(initialUnits || 'imperial');

    const calculationData = useMemo(() => ({
        rooms,
        units,
        totals: calculateTotals(rooms, units),
        roomResults: rooms.map((room) => ({
            id: room.id,
            results: calculateRoom(room, units)
        }))
    }), [rooms, units]);

    useEffect(() => {
        window.AlliedCalculatorData = calculationData;
    }, [calculationData]);

    useEffect(() => {
        if (!modalId) {
            return undefined;
        }

        const modalElement = document.getElementById(modalId);

        if (!modalElement) {
            return undefined;
        }

        const closeModal = () => {
            modalElement.classList.remove('is-open');
            modalElement.setAttribute('aria-hidden', 'true');
            document.body.classList.remove('cleanroom-modal-open');
        };

        const openModal = () => {
            modalElement.classList.add('is-open');
            modalElement.setAttribute('aria-hidden', 'false');
            document.body.classList.add('cleanroom-modal-open');
        };

        const onCloseClick = (event) => {
            const closeTarget = event.target.closest('[data-close-modal]');
            if (closeTarget) {
                closeModal();
            }
        };

        const onEscape = (event) => {
            if (event.key === 'Escape') {
                closeModal();
            }
        };

        modalElement.addEventListener('click', onCloseClick);
        document.addEventListener('keydown', onEscape);

        window.addEventListener(`allied:open-modal:${modalId}`, openModal);
        window.addEventListener(`allied:close-modal:${modalId}`, closeModal);

        return () => {
            modalElement.removeEventListener('click', onCloseClick);
            document.removeEventListener('keydown', onEscape);
            window.removeEventListener(`allied:open-modal:${modalId}`, openModal);
            window.removeEventListener(`allied:close-modal:${modalId}`, closeModal);
            closeModal();
        };
    }, [modalId]);

    const requestDetailedReport = () => {
        if (!modalId) {
            return;
        }

        window.dispatchEvent(new Event(`allied:open-modal:${modalId}`));
    };

    return (
        <CalculatorForm
            rooms={rooms}
            units={units}
            onUpdateRooms={setRooms}
            onUpdateUnits={setUnits}
            isEditor={false}
            onRequestDetailedReport={requestDetailedReport}
        />
    );
}

const hydrateCalculators = () => {
    const containers = document.querySelectorAll('.wp-block-allied-cleanroom-calculator-frontend');

    containers.forEach((container) => {
        if (container.dataset.hydrated === '1') {
            return;
        }

        const rawAttributes = container.dataset.attributes;

        if (!rawAttributes) {
            return;
        }

        try {
            const attributes = JSON.parse(rawAttributes);
            const rooms = Array.isArray(attributes.rooms) ? attributes.rooms : [];
            const units = attributes.units || 'imperial';
            const modalId = attributes.modalId || '';

            container.dataset.hydrated = '1';
            const root = createRoot(container);
            root.render(<FrontendCalculator initialRooms={rooms} initialUnits={units} modalId={modalId} />);
        } catch (error) {
            // Keep fallback markup visible if payload is invalid.
            // eslint-disable-next-line no-console
            console.error('Cleanroom calculator: invalid frontend attributes payload.', error);
        }
    });
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hydrateCalculators);
} else {
    hydrateCalculators();
}
